import { useEffect, useMemo, useState } from "react";
import { Plus, AlertTriangle } from "lucide-react";
import { Page, Panel, Table, DeleteButton, SearchInput, ExportMenu, FileSpreadsheet } from "../lib/ui";
import { money, can, download, api, rowLink } from "../lib/api";

const DEFAULT_LOW = 5;

export default function Stocks({ rows, setModal, onDelete, user }) {
  const [qName, setQName] = useState("");
  const [qDesc, setQDesc] = useState("");
  const [qBarcode, setQBarcode] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [globalMin, setGlobalMin] = useState(DEFAULT_LOW);
  const [filters, setFilters] = useState({
    unit: "", stockState: "all", category: "",
    barcodeOnly: false, minPrice: "", maxPrice: "",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setGlobalMin(Number(r.data?.low_stock_threshold ?? DEFAULT_LOW))).catch(() => {});
  }, []);

  const effectiveMin = (s) => (s.min_level === null || s.min_level === undefined || s.min_level === "") ? globalMin : Number(s.min_level);
  const isLow = (s) => Number(s.quantity || 0) <= effectiveMin(s);

  const categories = useMemo(() => Array.from(new Set(rows.map((s) => s.category).filter(Boolean))).sort(), [rows]);
  const units = useMemo(() => Array.from(new Set(rows.map((s) => s.unit).filter(Boolean))), [rows]);

  const filtered = useMemo(() => rows.filter((s) => {
    if (qName && !String(s.name || "").toLowerCase().includes(qName.toLowerCase())) return false;
    if (qDesc && !String(s.description || "").toLowerCase().includes(qDesc.toLowerCase())) return false;
    if (qBarcode && !String(s.barcode || "").toLowerCase().includes(qBarcode.toLowerCase())) return false;
    if (filters.unit && s.unit !== filters.unit) return false;
    if (filters.category && (s.category || "__none__") !== filters.category) return false;
    const qty = Number(s.quantity || 0);
    if (filters.stockState === "low" && !isLow(s)) return false;
    if (filters.stockState === "zero" && qty > 0) return false;
    if (filters.stockState === "in" && qty <= 0) return false;
    if (filters.barcodeOnly && !s.barcode) return false;
    if (filters.minPrice !== "" && Number(s.price || 0) < Number(filters.minPrice)) return false;
    if (filters.maxPrice !== "" && Number(s.price || 0) > Number(filters.maxPrice)) return false;
    return true;
  }), [rows, qName, qDesc, qBarcode, filters, globalMin]);

  const clearFilters = () => {
    setFilters({ unit: "", stockState: "all", category: "", barcodeOnly: false, minPrice: "", maxPrice: "" });
    setQName(""); setQDesc(""); setQBarcode("");
  };
  const activeFilterCount = [
    filters.unit, filters.category, filters.stockState !== "all" ? filters.stockState : "",
    filters.barcodeOnly ? "b" : "", filters.minPrice, filters.maxPrice,
    qName, qDesc, qBarcode,
  ].filter(Boolean).length;

  return (
    <>
      <Page
        eyebrow="STOK YÖNETİMİ"
        title="Stoklar"
        subtitle="Stok kodu, açıklama, miktar ve fiyatları güncel tutun."
        action={
          <div className="button-row">
            <ExportMenu testid="export-stocks" options={[
              { key: "xlsx", label: "Excel (.xlsx)", icon: <FileSpreadsheet size={14} />, onClick: () => download("/stocks/export?fmt=xlsx", "stoklar.xlsx") },
              { key: "csv", label: "CSV (.csv)", icon: <FileSpreadsheet size={14} />, onClick: () => download("/stocks/export?fmt=csv", "stoklar.csv") },
            ]} />
            <button data-testid="add-stocks-button" className="primary-button" disabled={!can(user, "create_stock")}
                    title={!can(user, "create_stock") ? "Bu işlem için yetkiniz yok" : ""}
                    onClick={() => setModal({ type: "stock" })}>
              <Plus size={17} /> Stok kartı
            </button>
          </div>
        }
      />
      <Panel
        className="module-panel"
        action={
          <div className="filter-row">
            <button type="button" data-testid="stocks-toggle-filters"
                    className={showFilters ? "secondary-button active" : "secondary-button"}
                    onClick={() => setShowFilters((v) => !v)}>
              Filtre {activeFilterCount > 0 && <b style={{marginLeft:6, background:"#eff6ff", color:"#2563eb", padding:"1px 6px", borderRadius:8, fontSize:10}}>{activeFilterCount}</b>}
            </button>
            <SearchInput value={qName} onChange={setQName} testid="stocks-search-name" placeholder="Stok adı ara" />
            <SearchInput value={qDesc} onChange={setQDesc} testid="stocks-search-desc" placeholder="Açıklama ara" />
            <SearchInput value={qBarcode} onChange={setQBarcode} testid="stocks-search-barcode" placeholder="Barkod ara" />
          </div>
        }
      >
        {showFilters && (
          <div className="advanced-filters" data-testid="stocks-advanced-filters">
            <label>
              <span>Birim</span>
              <select data-testid="stocks-filter-unit" value={filters.unit} onChange={(e) => setFilters({...filters, unit: e.target.value})}>
                <option value="">Tümü</option>
                {units.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label>
              <span>Kategori</span>
              <select data-testid="stocks-filter-category" value={filters.category} onChange={(e) => setFilters({...filters, category: e.target.value})}>
                <option value="">Tümü</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__none__">Kategorisiz</option>
              </select>
            </label>
            <label>
              <span>Stok durumu</span>
              <select data-testid="stocks-filter-state" value={filters.stockState} onChange={(e) => setFilters({...filters, stockState: e.target.value})}>
                <option value="all">Tümü</option>
                <option value="low">{`Kritik seviye (≤${globalMin})`}</option>
                <option value="zero">Sıfır stok</option>
                <option value="in">Stokta var</option>
              </select>
            </label>
            <label>
              <span>Min fiyat</span>
              <input type="number" step="0.01" data-testid="stocks-filter-minprice" value={filters.minPrice}
                     onChange={(e) => setFilters({...filters, minPrice: e.target.value})} />
            </label>
            <label>
              <span>Max fiyat</span>
              <input type="number" step="0.01" data-testid="stocks-filter-maxprice" value={filters.maxPrice}
                     onChange={(e) => setFilters({...filters, maxPrice: e.target.value})} />
            </label>
            <label className="check-inline">
              <input type="checkbox" data-testid="stocks-filter-barcodeonly" checked={filters.barcodeOnly}
                     onChange={(e) => setFilters({...filters, barcodeOnly: e.target.checked})} />
              Sadece barkodlu
            </label>
            <button type="button" className="text-button" onClick={clearFilters} data-testid="stocks-filter-clear">Temizle</button>
          </div>
        )}
        <Table
          testid="stocks-table"
          columns={[
            { label: "Stok kodu", key: "code", sort: (s) => s.code }, { label: "Stok adı", key: "name", sort: (s) => s.name },
            { label: "Kategori", key: "category", sort: (s) => s.category || "" }, { label: "Barkod", key: "barcode", sort: (s) => s.barcode || "" },
            { label: "Açıklama" },
            { label: "Miktar", key: "quantity", sort: (s) => Number(s.quantity || 0) }, { label: "Kritik" },
            { label: "Birim fiyat", align: "right", key: "price", sort: (s) => Number(s.price || 0) },
            { label: "KDV", align: "right", key: "kdv", sort: (s) => Number(s.kdv || 0) }, { label: "" },
          ]}
          rows={filtered}
          empty="Henüz stok kartı yok."
          renderRow={(s) => {
            const low = isLow(s);
            const min = effectiveMin(s);
            const custom = s.min_level !== null && s.min_level !== undefined && s.min_level !== "";
            return (
              <tr key={s.id} data-testid={`stock-row-${s.id}`} className={low ? "row-warn clickable" : "clickable"}
                  onClick={() => setModal({ type: "stock-movements", stockId: s.id })}>
                <td><strong className="code">{s.code}</strong></td>
                <td {...rowLink(`/detay/stok/${s.id}`, true)}
                    style={{ cursor: "pointer" }}>
                  <a href={`/detay/stok/${s.id}`} className="row-anchor" onClick={(e) => e.stopPropagation()}><strong style={{ textDecoration: "underline dotted #94a3b8" }}>{s.name}</strong></a>
                  {low && (
                    <small className="warn-tag" data-testid={`low-stock-${s.id}`}>
                      <AlertTriangle size={10} /> Kritik seviye
                    </small>
                  )}
                </td>
                <td>{s.category ? <span className="type-tag corporate" data-testid={`stock-category-${s.id}`}>{s.category}</span> : "—"}</td>
                <td><span className="code" data-testid={`stock-barcode-${s.id}`}>{s.barcode || "—"}</span></td>
                <td>{s.description || "—"}</td>
                <td className={low ? "negative" : ""}>{s.quantity} {s.unit}</td>
                <td data-testid={`stock-min-${s.id}`}>
                  {min} {s.unit}
                  {!custom && <small style={{ display: "block", color: "#94a3b8", fontSize: 9 }}>genel</small>}
                </td>
                <td className="amount">{money(s.price)}</td>
                <td className="amount" data-testid={`stock-kdv-${s.id}`}>%{s.kdv ?? 20}</td>
                <td className="amount" onClick={(e) => e.stopPropagation()}>
                  <DeleteButton
                    testid={`delete-stock-${s.id}`}
                    disabled={!can(user, "delete_stock")}
                    onClick={() => onDelete(s)}
                  />
                </td>
              </tr>
            );
          }}
        />
      </Panel>
    </>
  );
}
